<template>
  <BaseScreen v-bind="screen">
    <BaseAlert
      v-if="isThanksVisible"
      v-bind="thanks"
      slot="middle"
    />
    <BaseAlert
      v-if="isErrorVisible"
      v-bind="error"
      slot="middle"
    />
    <BaseForm
      ref="form"
      v-bind="form"
      class="screen__form"
      @submit="onFormSubmit"
    />
    <ModalConfirm
      v-if="isAbortModalVisible"
      v-bind="abortModal"
      class="screen__overlay"
      slot="root"
      @disagree="hideAbortModal"
      @agree="abortEdit"
    />
  </BaseScreen>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import CONSTANTS from '@/helpers/constants';

import screenMixin from '@/mixins/screen';
import formMixin from '@/mixins/form';
import FieldEmail from '@/components/FieldEmail.vue';
import FieldPhone from '@/components/FieldPhone.vue';
import ModalConfirm from '@/components/ModalConfirm.vue';

export default {
  name: 'PersonalProfileScreen',
  mixins: [screenMixin, formMixin],
  components: {
    ModalConfirm,
  },
  data() {
    return {
      isAbortModalVisible: false,
      values: {},
    };
  },
  computed: {
    ...mapState('personal', [
      'profile',
    ]),
    titleText() {
      return this.$t('titles.editPersonalInfo');
    },
    pendingText() {
      return this.$t('pending.edit');
    },
    screenCase() {
      if (this.isThanksVisible) {
        return CONSTANTS.STEP_PROFILE_UPDATED;
      }
      if (this.isErrorVisible) {
        return CONSTANTS.STEP_UPDATE_FAILED;
      }
      if (this.isAbortModalVisible) {
        return CONSTANTS.STEP_CONFIRM_LEAVE;
      }
      if (!this.isPending) {
        return CONSTANTS.STEP_PROFILE;
      }
      return null;
    },
    retryAction() {
      return this.onFormSubmit;
    },
    form() {
      return {
        values: this.values,
        fields: [
          {
            is: FieldEmail,
            name: 'email',
            required: this.values.email || !this.values.phone,
          },
          {
            is: FieldPhone,
            name: 'phone',
            required: this.values.phone || !this.values.email,
          },
        ],
        actions: [
          this.noChanges ? null : {
            text: this.$t('actions.cancel'),
            action: this.goToBackScreen,
            view: 'outlined',
          },
          {
            type: 'submit',
            text: this.$t('actions.save'),
            disabled: this.noChanges,
          },
        ].filter(Boolean),
      };
    },
    thanks() {
      return {
        icon: {
          name: 'success',
          theme: 'primary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.personalInfoWasChanged'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.backToPersonal'),
            action: this.goToBackScreen,
          },
        ],
      };
    },
    error() {
      return {
        icon: {
          name: 'important',
          theme: 'secondary',
          size: 'l',
        },
        title: {
          text: this.$t('titles.badConnection'),
          size: 'l',
        },
        description: {
          text: this.$t('texts.editPersonalInfoFailed'),
          size: 'l',
        },
        actions: [
          {
            text: this.$t('actions.home'),
            action: this.goToHomeScreen,
            view: 'outlined',
          },
          {
            text: this.$t('actions.retry'),
            action: this.retryErrorAction,
          },
        ],
      };
    },
    abortModal() {
      return {
        title: {
          text: this.$t('titles.wantToLeaveScreen'),
          size: 'l',
          isNormal: true,
          isCenter: true,
        },
        agreeText: this.$t('actions.yesLeave'),
        disagreeText: this.$t('actions.noStay'),
        isInversed: true,
      };
    },
    noChanges() {
      if (!this.profile) return true;
      return Object.entries(this.values)
        .every(([key, value]) => (this.profile[key] === value));
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isErrorVisible || this.noChanges) {
      next();
    } else {
      this.showAbortModal();
    }
  },
  created() {
    this.setProfileValues();
    this.initActions([
      [CONSTANTS.ACTION_DISAGREE, this.hideAbortModal],
      [CONSTANTS.ACTION_AGREE, this.abortEdit],
      [CONSTANTS.ACTION_BACK, this.goToBackScreen],
    ]);
  },
  methods: {
    ...mapActions('personal', [
      'updateProfile',
    ]),
    async onFormSubmit() {
      try {
        this.showPending();
        await this.updateProfile(this.values);
        this.showThanks();
      } catch (e) {
        console.error('onFormSubmit error: ', e);
        this.showError();
      }
    },
    abortEdit() {
      this.setProfileValues();
      this.goToBackScreen();
    },
    setProfileValues() {
      this.values = { ...this.profile };
    },
    beforeInteractionStopped() {
      this.setProfileValues();
    },
    showAbortModal() {
      this.isAbortModalVisible = true;
    },
    hideAbortModal() {
      this.isAbortModalVisible = false;
    },
  },
};
</script>
